/**
 * Trang xo so.
 *
 * Bon diem nghiep vu man hinh nay phai the hien, va deu la thu nguoi choi hoi
 * dau tien khi co tranh chap:
 *
 *   1. GIO DONG BAN, khong phai gio quay. Hai moc nay khac nhau; nguoi choi
 *      tuong con kip vi thay gio quay con xa la nguon khieu nai kinh dien.
 *   2. TRAN CHI TRA MOI VE (LOT-R33). Bang thuong ghi boi so rat lon, nhung
 *      tran chi tra chan tren no. Giau tran di la ban thu khong dinh gia dung.
 *   3. BAM HAT GIONG cong bo TRUOC khi mo ban (LOT-R51). Do la toan bo co so
 *      de noi ky quay khong bi can thiep — phai o ngay tren man hinh mua ve.
 *   4. Bang thuong cua dung kieu choi dang chon. Doi kieu choi thi boi so doi
 *      hoan toan; hien bang cua kieu khac la noi sai.
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  newIdempotencyKey,
  type LotteryDraw,
  type LotteryLine,
  type LotteryPlayType,
  type LotteryTicketCreated,
} from '../lib/api';
import { formatMoney, parseAmount } from '../lib/money';
import { useSession } from '../lib/session';
import { Badge, Button, Card, EmptyState, ErrorList, Skeleton } from '../components/ui';
import {
  BoardPrizeTable,
  GroupMarkets,
  NumberGrid,
  padNumber,
  ResultBoard,
} from './LotteryBoard';
import styles from './Lottery.module.css';

const PLAY_TYPE_LABEL: Readonly<Record<string, string>> = {
  EXACT: 'Đúng thứ tự',
  ANY_ORDER: 'Đúng tập số',
  PICK_K_OF_N: 'Chọn K trong N',
  KENO: 'Keno',
  GROUP: 'Chẵn lẻ · Tài xỉu',
  LO: 'Lô',
  DE: 'Đề',
};

/**
 * Dieu kien thang cua tung kieu choi, dat ngay canh cho chon.
 *
 * Lô va Đề dat tren cung mot con so hai chu so nhung thang theo hai luat khac
 * han nhau. Khong noi ro thi nguoi choi doc bang thuong thay Đề tra gap hai muoi
 * bay lan Lô va ket luan Lô la lua chon kem — trong khi thuc te Đề chi doi
 * chieu voi mot giai, con Lô doi chieu voi ca hai muoi bay giai.
 */
const PLAY_TYPE_NOTE: Readonly<Record<string, string>> = {
  EXACT: 'Số phải trùng cả giá trị lẫn vị trí.',
  ANY_ORDER: 'Chỉ cần trùng tập số, thứ tự không tính.',
  KENO: 'Trúng càng nhiều số thì bậc thưởng càng cao.',
  DE: 'Trúng khi hai chữ số cuối của giải đặc biệt đúng bằng số đã chọn. Chỉ đối chiếu với giải đặc biệt, các giải khác không tính.',
  LO: 'Trúng khi số đã chọn trùng hai chữ số cuối của bất kỳ giải nào trong bảng kết quả. Xuất hiện càng nhiều lần thì thưởng càng cao.',
  GROUP: 'Đặt theo tính chất của số chứ không đặt vào một con số cụ thể.',
};

const TICKET_STATUS: Readonly<Record<string, { label: string; tone: 'neutral' | 'positive' | 'negative' | 'warning' }>> = {
  ACTIVE: { label: 'Chờ quay', tone: 'warning' },
  WON: { label: 'Trúng thưởng', tone: 'positive' },
  LOST: { label: 'Không trúng', tone: 'neutral' },
  VOID: { label: 'Hủy', tone: 'neutral' },
  PAID: { label: 'Đã chi trả', tone: 'positive' },
};

export function LotteryPage(): JSX.Element {
  const { isAuthenticated, me, refresh } = useSession();
  const queryClient = useQueryClient();

  const [productCode, setProductCode] = useState<string | null>(null);
  const [drawId, setDrawId] = useState<string | null>(null);
  const [playType, setPlayType] = useState<LotteryPlayType | null>(null);
  const [slots, setSlots] = useState<(number | null)[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [stake, setStake] = useState('');
  const [lines, setLines] = useState<LotteryLine[]>([]);
  const [bought, setBought] = useState<LotteryTicketCreated | null>(null);

  /** Chon nhieu so cung luc cho Lô va Đề; moi so thanh mot dong cuoc rieng. */
  const [boardPicks, setBoardPicks] = useState<number[]>([]);
  /** Lua chon cua cuoc nhom, dang "<ma cua>:<ma lua chon>". */
  const [groupSel, setGroupSel] = useState<string | null>(null);
  /** Ve dang mo xem chi tiet ket qua. */
  const [openTicket, setOpenTicket] = useState<string | null>(null);

  const currency = me?.currency ?? 'USD';

  const products = useQuery({
    queryKey: ['lottery-products'],
    queryFn: api.lottery.products,
    enabled: isAuthenticated,
  });

  const product = useMemo(
    () => products.data?.products.find((p) => p.code === productCode) ?? null,
    [products.data, productCode],
  );

  const draws = useQuery({
    queryKey: ['lottery-draws', productCode],
    queryFn: () => api.lottery.draws(productCode ?? undefined),
    enabled: isAuthenticated && productCode !== null,
    // Ky quay dong ban theo dong ho; danh sach phai tuoi.
    refetchInterval: 30_000,
  });

  const tickets = useQuery({
    queryKey: ['lottery-tickets'],
    queryFn: api.lottery.tickets,
    enabled: isAuthenticated,
  });

  // Chon san pham dau tien va kieu choi dau tien khi du lieu ve.
  useEffect(() => {
    if (productCode === null && products.data?.products[0]) {
      setProductCode(products.data.products[0].code);
    }
  }, [productCode, products.data]);

  useEffect(() => {
    if (!product) return;
    setPlayType(product.play_types[0] ?? null);
    setSlots(new Array<number | null>(product.number_config.pick).fill(null));
    setPicked([]);
    setBoardPicks([]);
    setGroupSel(null);
    setLines([]);
  }, [product]);

  // Doi kieu choi thi bo lua chon dang do: mot con so dang chon cho Đề khong
  // mang y nghia gi khi da chuyen sang cua cuoc nhom.
  useEffect(() => {
    setBoardPicks([]);
    setGroupSel(null);
  }, [playType]);

  /**
   * Ky quay dat duoc phai thoa CA HAI: trang thai da mo ban VA chua toi gio dong.
   * Danh sach con chua ca ky da len lich cho gio mo — hien de nguoi choi biet
   * ky sau vao luc nao, nhung dat vao do thi may chu tu choi.
   */
  const openDraw = useMemo(() => {
    const list = draws.data?.draws ?? [];
    return (
      list.find((d) => d.status === 'OPEN' && new Date(d.closes_at).getTime() > Date.now()) ?? null
    );
  }, [draws.data]);

  useEffect(() => {
    if (openDraw && drawId === null) setDrawId(openDraw.draw_id);
  }, [openDraw, drawId]);

  const draw = useMemo(
    () => draws.data?.draws.find((d) => d.draw_id === drawId) ?? openDraw,
    [draws.data, drawId, openDraw],
  );

  /**
   * Ky sap toi, hien de nguoi choi biet lan sau vao luc nao.
   *
   * Loc bo ky da qua gio dong ban. Danh sach may chu tra ve theo TRANG THAI, ma
   * trang thai do tac vu nen cap nhat; neu tac vu do tre mot nhip thi ky da het
   * gio van con nam trong danh sach. Doi chieu voi dong ho ngay tai day thi man
   * hinh khong bao gio quang cao mot ky da dong.
   */
  const upcoming = useMemo(
    () =>
      (draws.data?.draws ?? [])
        .filter((d) => d.draw_id !== draw?.draw_id && new Date(d.closes_at).getTime() > Date.now())
        .slice(0, 3),
    [draws.data, draw],
  );

  const config = product?.number_config;
  const currentNumbers = useMemo(
    () => (config?.allow_repeats ? slots.filter((n): n is number => n !== null) : picked),
    [config, slots, picked],
  );
  const numbersComplete = config ? currentNumbers.length === config.pick : false;

  const stakeMinor = useMemo(() => {
    try {
      return stake ? parseAmount(stake, currency) : 0n;
    } catch {
      return 0n;
    }
  }, [stake, currency]);

  const quote = useQuery({
    queryKey: ['lottery-quote', productCode, JSON.stringify(lines)],
    queryFn: () => api.lottery.quote(productCode!, lines),
    enabled: isAuthenticated && productCode !== null && lines.length > 0,
  });

  const buy = useMutation({
    mutationFn: () => api.lottery.buy(drawId!, lines, newIdempotencyKey()),
    onSuccess: (result) => {
      setBought(result);
      setLines([]);
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['lottery-tickets'] });
    },
  });

  if (!isAuthenticated) return <EmptyState title="Đăng nhập để mua vé" />;

  if (products.isLoading) {
    return (
      <div className={styles.page}>
        <Skeleton height={200} />
      </div>
    );
  }

  if (bought) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Đã mua vé</h1>
        <Card>
          <div className={styles.bought}>
            <Row label="Số dòng" value={String(bought.lines.length)} />
            <Row label="Tổng tiền" value={`${formatMoney(bought.total_stake)} ${currency}`} />
            <Row
              label="Trần chi trả của vé"
              value={`${formatMoney(bought.max_payout)} ${currency}`}
              strong
            />
            <p className={styles.note}>
              Kết quả được đối chiếu tự động sau khi kỳ quay công bố. Tiền trúng thưởng vào thẳng
              số dư khả dụng.
            </p>
            <Button variant="secondary" onClick={() => setBought(null)}>Mua vé khác</Button>
          </div>
        </Card>
      </div>
    );
  }

  const playTypes = product?.play_types ?? [];
  const prizeTiers = product && playType ? (product.prize_table.play_types[playType] ?? []) : [];

  /** San pham choi tren bang nhieu giai dung bo chon so hoan toan khac. */
  const board = product?.board_config ?? null;
  const isBoard = board !== null && playType !== null && ['LO', 'DE', 'GROUP'].includes(playType);

  const belowMin =
    product !== null && stakeMinor > 0n && stakeMinor < BigInt(product.min_line_stake.amount_minor);
  const aboveMax =
    product !== null && stakeMinor > BigInt(product.max_line_stake.amount_minor);
  const error = buy.error instanceof ApiError ? buy.error : null;

  /**
   * Da chon du thu can thiet de them dong cuoc chua.
   *
   * Voi Lô va Đề, "du" nghia la chon it nhat mot so — khong co so luong co dinh
   * nhu Keno, vi dat mot lo so cung mot muc tien la cach dat thong thuong.
   */
  const boardReady =
    playType === 'GROUP' ? groupSel !== null : boardPicks.length > 0;
  const stakeReady = stakeMinor > 0n && !belowMin && !aboveMax;

  const addLine = (): void => {
    if (!playType || !stakeReady) return;

    if (isBoard) {
      if (!boardReady) return;

      // Cua cuoc nhom sinh mot dong; Lô va Đề sinh MOT DONG MOI SO da chon.
      const added: LotteryLine[] =
        playType === 'GROUP'
          ? [{ play_type: playType, numbers: [], stake_minor: stakeMinor.toString(), selection: groupSel! }]
          : boardPicks.map((n) => ({
              play_type: playType,
              numbers: [n],
              stake_minor: stakeMinor.toString(),
            }));

      setLines((prev) => [...prev, ...added]);
      setBoardPicks([]);
      setGroupSel(null);
      return;
    }

    if (!numbersComplete) return;
    setLines((prev) => [
      ...prev,
      { play_type: playType, numbers: currentNumbers, stake_minor: stakeMinor.toString() },
    ]);
    if (config?.allow_repeats) setSlots(new Array<number | null>(config.pick).fill(null));
    else setPicked([]);
  };

  /** Nhan doc duoc cua mot dong cuoc trong danh sach ve dang soan. */
  const lineLabel = (line: LotteryLine): string => {
    if (line.play_type === 'GROUP' && line.selection && board) {
      const [marketCode, outcomeCode] = line.selection.split(':');
      const market = board.group_markets.find((m) => m.code === marketCode);
      const outcome = market?.outcomes.find((o) => o.code === outcomeCode);
      return market && outcome ? `${market.name} · ${outcome.name}` : line.selection;
    }
    return PLAY_TYPE_LABEL[line.play_type] ?? line.play_type;
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Xổ số</h1>

      <div className={styles.productTabs}>
        {products.data?.products.map((p) => (
          <button
            key={p.code}
            className={[styles.productTab, p.code === productCode ? styles.productTabActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setProductCode(p.code);
              setDrawId(null);
            }}
          >
            {p.name}
          </button>
        ))}
      </div>

      {product && (
        <>
          {draw ? (
            <DrawCard draw={draw} productKind={product.kind} upcoming={upcoming} />
          ) : (
            <Card>
              <p className={styles.muted}>Chưa có kỳ quay nào đang mở bán.</p>
            </Card>
          )}

          <Card title="Kiểu chơi">
            <div className={styles.playTypes}>
              {playTypes.map((t) => (
                <button
                  key={t}
                  className={[styles.playType, t === playType ? styles.playTypeActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setPlayType(t)}
                >
                  {PLAY_TYPE_LABEL[t] ?? t}
                </button>
              ))}
            </div>
            {playType && PLAY_TYPE_NOTE[playType] && (
              <p className={styles.note}>{PLAY_TYPE_NOTE[playType]}</p>
            )}

            {/* Bang thuong DOI theo kieu choi — hien dung bang cua kieu dang chon.
                Voi Lô va Đề, bang con quy boi so ra so tien thuc nhan tren muc
                cuoc dang nhap: mot boi so tran khong noi duoc gi cho den khi
                nguoi choi nhin thay no thanh tien. */}
            {isBoard && playType !== 'GROUP' ? (
              <BoardPrizeTable
                playType={playType!}
                tiers={prizeTiers}
                stakeMinor={stakeMinor}
                currency={currency}
              />
            ) : (
              prizeTiers.length > 0 && (
                <table className={styles.prizeTable}>
                  <thead>
                    <tr>
                      <th>Trúng</th>
                      <th>Bội số</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prizeTiers.map((tier) => (
                      <tr key={tier.tier}>
                        <td>{tier.matched} số</td>
                        <td className="numeric">×{(tier.multiplier_bp / 10_000).toLocaleString('vi-VN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}

            <p className={styles.cap}>
              Trần chi trả mỗi vé{' '}
              <strong className="numeric">
                {formatMoney(product.max_payout_per_ticket)} {currency}
              </strong>
              . Bội số bên trên vẫn bị trần này chặn trên.
            </p>
          </Card>

          <Card
            title={
              isBoard
                ? playType === 'GROUP'
                  ? 'Chọn cửa'
                  : 'Chọn số'
                : `Chọn ${config!.pick} số`
            }
          >
            {isBoard ? (
              playType === 'GROUP' ? (
                <GroupMarkets
                  board={board!}
                  selected={groupSel}
                  stakeMinor={stakeMinor}
                  currency={currency}
                  onSelect={(value) => setGroupSel((prev) => (prev === value ? null : value))}
                />
              ) : (
                <NumberGrid
                  min={config!.min}
                  max={config!.max}
                  digits={board!.tail_digits}
                  picked={boardPicks}
                  onChange={setBoardPicks}
                />
              )
            ) : config!.allow_repeats ? (
              <SlotPicker config={config!} slots={slots} onChange={setSlots} />
            ) : (
              <GridPicker config={config!} picked={picked} onChange={setPicked} />
            )}

            <div className={styles.stakeRow}>
              <input
                className={`numeric ${styles.stakeInput}`}
                inputMode="decimal"
                placeholder="Tiền mỗi dòng"
                value={stake}
                onChange={(e) => setStake(e.target.value)}
                aria-label="Tiền cược mỗi dòng"
              />
              <span className={styles.stakeCurrency}>{currency}</span>
              <Button
                size="sm"
                onClick={addLine}
                disabled={isBoard ? !boardReady || !stakeReady : !numbersComplete || !stakeReady}
              >
                {/* Voi Lô va Đề, mot lan bam sinh ra nhieu dong — nut phai noi
                    dung so dong sap them, khong noi chung chung "Thêm dòng". */}
                {isBoard && playType !== 'GROUP' && boardPicks.length > 1
                  ? `Thêm ${boardPicks.length} dòng`
                  : 'Thêm dòng'}
              </Button>
            </div>

            {belowMin && (
              <p className={styles.warn}>
                Tối thiểu {formatMoney(product.min_line_stake)} {currency} mỗi dòng
              </p>
            )}
            {aboveMax && (
              <p className={styles.warn}>
                Tối đa {formatMoney(product.max_line_stake)} {currency} mỗi dòng
              </p>
            )}
            {isBoard ? (
              !boardReady && (
                <p className={styles.note}>
                  {playType === 'GROUP'
                    ? 'Chọn một cửa để đặt cược.'
                    : 'Chọn ít nhất một số. Chọn nhiều số cùng lúc thì mỗi số thành một dòng riêng ở cùng mức tiền.'}
                </p>
              )
            ) : (
              !numbersComplete && (
                <p className={styles.note}>
                  Còn thiếu {config!.pick - currentNumbers.length} số.
                </p>
              )
            )}
          </Card>

          {lines.length > 0 && (
            <Card title={`Vé đang soạn — ${lines.length} dòng`}>
              <ul className={styles.lineList}>
                {lines.map((line, i) => (
                  <li key={i} className={styles.line}>
                    <span className={styles.lineNumbers}>
                      {line.numbers.map((n, j) => (
                        <span key={j} className={`numeric ${styles.lineBall}`}>
                          {/* So hai chu so phai doc la "07", khong phai "7". */}
                          {board ? padNumber(n, board.tail_digits) : n}
                        </span>
                      ))}
                    </span>
                    <span className={styles.lineMeta}>{lineLabel(line)}</span>
                    <span className={`numeric ${styles.lineStake}`}>
                      {formatMoney({ minor: BigInt(line.stake_minor), currency })}
                    </span>
                    <button
                      className={styles.lineRemove}
                      onClick={() => setLines((prev) => prev.filter((_, k) => k !== i))}
                      aria-label={`Bỏ dòng ${i + 1}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>

              <div className={styles.summary}>
                <Row
                  label="Tổng tiền"
                  value={
                    quote.data?.quote
                      ? `${formatMoney(quote.data.quote.total_stake)} ${currency}`
                      : '—'
                  }
                />
                <Row
                  label="Nhận được nhiều nhất"
                  value={
                    quote.data?.quote
                      ? `${formatMoney(quote.data.quote.max_payout)} ${currency}`
                      : '—'
                  }
                  strong
                />
                <Row
                  label="Trần chi trả của vé"
                  value={
                    quote.data?.quote
                      ? `${formatMoney(quote.data.quote.max_payout_per_ticket)} ${currency}`
                      : `${formatMoney(product.max_payout_per_ticket)} ${currency}`
                  }
                />
              </div>

              {/*
                LOT-R33: tran chi tra cat truoc boi so bang thuong. Noi dieu nay
                TRUOC khi nguoi choi tra tien, khong phai sau khi ho trung roi
                moi phat hien boi so tren bang thuong khong ap dung het.
              */}
              {quote.data?.quote?.capped && (
                <p className={styles.warn}>
                  Bảng thưởng cho ra{' '}
                  <strong className="numeric">
                    {formatMoney(quote.data.quote.uncapped_payout)} {currency}
                  </strong>
                  , nhưng trần chi trả mỗi vé chặn ở{' '}
                  <strong className="numeric">
                    {formatMoney(quote.data.quote.max_payout)} {currency}
                  </strong>
                  . Tách thành nhiều vé thì mỗi vé có trần riêng.
                </p>
              )}

              {error && <ErrorList messages={error.messages} />}

              <Button
                size="lg"
                fullWidth
                loading={buy.isPending}
                disabled={drawId === null || quote.isLoading}
                onClick={() => buy.mutate()}
              >
                Mua vé
              </Button>
              {drawId === null && (
                <p className={styles.warn}>Chưa có kỳ quay nào đang mở bán để đặt vé.</p>
              )}
            </Card>
          )}
        </>
      )}

      <Card title="Vé đã mua">
        {tickets.isLoading ? (
          <Skeleton height={60} />
        ) : tickets.data && tickets.data.tickets.length > 0 ? (
          <ul className={styles.ticketList}>
            {tickets.data.tickets.map((t) => {
              const status = TICKET_STATUS[t.status] ?? { label: t.status, tone: 'neutral' as const };
              const open = openTicket === t.ticket_id;
              return (
                <li key={t.ticket_id}>
                  {/* Ca hang la mot nut: doi chieu ket qua la viec nguoi choi
                      lam thuong xuyen nhat sau khi mua, nen no khong nen nam sau
                      mot bieu tuong nho o cuoi hang. */}
                  <button
                    type="button"
                    className={styles.ticket}
                    onClick={() => setOpenTicket(open ? null : t.ticket_id)}
                    aria-expanded={open}
                  >
                    <span className={styles.ticketDate}>
                      {new Date(t.purchased_at).toLocaleString('vi-VN', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span className={`numeric ${styles.ticketStake}`}>
                      {formatMoney(t.stake)} {currency}
                    </span>
                    <Badge tone={status.tone}>{status.label}</Badge>
                    <span className={`numeric ${styles.ticketPayout}`}>
                      {t.payout ? `+${formatMoney(t.payout)}` : ''}
                    </span>
                  </button>

                  {open && <TicketDetail ticketId={t.ticket_id} currency={currency} />}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={styles.muted}>Chưa có vé nào.</p>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Chi tiet mot ve: tung dong cuoc, ket qua cua no, va BANG KET QUA goc.
 *
 * Phan quan trong nhat la bang ket qua co danh dau vi tri trung. Mot dong chu
 * "Không trúng" khong tra loi duoc cau hoi nguoi choi thuc su hoi — so cua ho co
 * tren bang khong, va o giai nao. Khi ho tu doi chieu duoc bang mat thi mot
 * phan lon khieu nai khong con ly do de gui di.
 */
function TicketDetail({ ticketId, currency }: { ticketId: string; currency: string }): JSX.Element {
  const detail = useQuery({
    queryKey: ['lottery-ticket', ticketId],
    queryFn: () => api.lottery.ticket(ticketId),
  });

  if (detail.isLoading) return <Skeleton height={80} />;

  const ticket = detail.data?.ticket;
  if (!ticket) return <p className={styles.muted}>Không đọc được chi tiết vé.</p>;

  return (
    <div className={styles.detail}>
      <div className={styles.detailHead}>
        <span className={styles.muted}>
          {ticket.product_name} · Kỳ {ticket.draw_number}
        </span>
      </div>

      <ul className={styles.detailLines}>
        {ticket.lines.map((line, i) => (
          <li key={i} className={styles.detailLine}>
            <span className={styles.lineMeta}>
              {line.selection_label ?? PLAY_TYPE_LABEL[line.play_type] ?? line.play_type}
            </span>
            <span className={`numeric ${styles.lineNumbers}`}>
              {line.numbers.map((n, j) => (
                <span key={j} className={`numeric ${styles.lineBall}`}>
                  {padNumber(n, 2)}
                </span>
              ))}
            </span>
            <span className={styles.detailOutcome}>
              {line.matched === null
                ? 'Chờ kết quả'
                : line.play_type === 'LO'
                  ? `${line.matched} nháy`
                  : line.matched > 0
                    ? 'Trúng'
                    : 'Không trúng'}
            </span>
            <span className={`numeric ${styles.lineStake}`}>
              {line.payout ? `+${formatMoney(line.payout)} ${currency}` : ''}
            </span>
          </li>
        ))}
      </ul>

      {ticket.result_board ? (
        <>
          <p className={styles.note}>
            Bảng kết quả kỳ {ticket.draw_number}. Ô được tô là giải mà số trên vé trùng hai chữ số
            cuối.
          </p>
          <ResultBoard
            cells={ticket.result_board}
            highlight={ticket.lines.flatMap((l) => l.hit_positions ?? [])}
          />
        </>
      ) : (
        <p className={styles.note}>
          Kỳ quay chưa công bố kết quả. Bảng kết quả hiện ngay tại đây sau khi công bố.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The ky quay.
 *
 * Dem nguoc theo GIO DONG BAN chu khong phai gio quay: do la moc thuc su chan
 * nguoi choi lai. Bam hat giong dat ngay day, kem mot cau giai thich no de lam gi
 * — cong bo mot chuoi bam ma khong noi y nghia thi khong ai kiem chung.
 */
function DrawCard({
  draw,
  productKind,
  upcoming,
}: {
  draw: LotteryDraw;
  productKind: string;
  upcoming: LotteryDraw[];
}): JSX.Element {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const closesIn = new Date(draw.closes_at).getTime() - now;
  const closed = closesIn <= 0;

  return (
    <Card>
      <div className={styles.draw}>
        <div className={styles.drawTop}>
          <span className={styles.drawNumber}>Kỳ {draw.draw_number}</span>
          <Badge tone={closed ? 'negative' : 'positive'}>
            {closed ? 'Đã đóng bán' : `Đóng bán sau ${formatDuration(closesIn)}`}
          </Badge>
        </div>
        <span className={styles.drawTime}>
          Quay lúc{' '}
          <span className="numeric">
            {new Date(draw.draws_at).toLocaleString('vi-VN', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </span>

        {/* Hai loai san pham chung minh tinh cong bang theo hai cach khac nhau.
            Chi hien bang chung cua loai nay ma im lang ve loai kia thi nguoi choi
            tuong san pham con lai khong co gi bao dam. */}
        {draw.seed_hash ? (
          <details className={styles.seed}>
            <summary className={styles.seedSummary}>Bằng chứng công bằng</summary>
            <p className={styles.note}>
              Chuỗi dưới đây là bản băm của hạt giống sinh số, công bố <strong>trước</strong> khi
              mở bán. Sau khi quay, hạt giống gốc được công bố; băm lại hạt giống đó phải ra đúng
              chuỗi này thì kỳ quay mới hợp lệ.
            </p>
            <code className={styles.seedHash}>{draw.seed_hash}</code>
          </details>
        ) : productKind === 'EXTERNAL_RESULT' ? (
          <p className={styles.note}>
            Kết quả kỳ này lấy theo kỳ quay do đơn vị bên ngoài công bố, và phải được nhập khớp
            hai lần độc lập mới ghi nhận. Lệch nhau thì kỳ quay chuyển sang tranh chấp và không
            đối chiếu vé.
          </p>
        ) : null}

        {upcoming.length > 0 && (
          <div className={styles.upcoming}>
            <span className={styles.upcomingLabel}>Kỳ tiếp theo</span>
            {upcoming.map((next) => (
              <span key={next.draw_id} className={styles.upcomingItem}>
                Kỳ {next.draw_number} · đóng bán{' '}
                <span className="numeric">
                  {new Date(next.closes_at).toLocaleString('vi-VN', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

// ---------------------------------------------------------------------------

/**
 * Bo so cho phep trung nhau: moi vi tri chon rieng, vi thu tu co y nghia.
 *
 * Cap nhat theo DANG HAM (`setSlots(prev => ...)`) chu khong dung gia tri dang
 * co san. Hai cu bam sat nhau — bam doi tren dien thoai, hoac nguoi choi go
 * nhanh ca ba vi tri — roi vao cung mot nhip dung hinh; doc gia tri cu thi cu
 * bam sau ghi de cu bam truoc va mot vi tri lang le mat lua chon.
 */
function SlotPicker({
  config,
  slots,
  onChange,
}: {
  config: { pick: number; min: number; max: number };
  slots: (number | null)[];
  onChange: (update: (prev: (number | null)[]) => (number | null)[]) => void;
}): JSX.Element {
  const digits = range(config.min, config.max);

  return (
    <div className={styles.slots}>
      {slots.map((value, index) => (
        <div key={index} className={styles.slot}>
          <span className={styles.slotLabel}>Vị trí {index + 1}</span>
          <div className={styles.slotDigits}>
            {digits.map((d) => (
              <button
                key={d}
                className={[styles.ball, value === d ? styles.ballActive : ''].filter(Boolean).join(' ')}
                onClick={() =>
                  onChange((prev) => {
                    const next = [...prev];
                    next[index] = prev[index] === d ? null : d;
                    return next;
                  })
                }
                aria-pressed={value === d}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Bo so khong trung nhau: luoi chon nhieu, chan khi du so luong. */
function GridPicker({
  config,
  picked,
  onChange,
}: {
  config: { pick: number; min: number; max: number };
  picked: number[];
  onChange: (update: (prev: number[]) => number[]) => void;
}): JSX.Element {
  const numbers = range(config.min, config.max);
  const full = picked.length >= config.pick;

  return (
    <>
      <div className={styles.grid}>
        {numbers.map((n) => {
          const on = picked.includes(n);
          return (
            <button
              key={n}
              className={[styles.ball, on ? styles.ballActive : ''].filter(Boolean).join(' ')}
              // Da du so thi khoa cac o con lai: ro rang hon la cho bam roi bo qua.
              disabled={!on && full}
              onClick={() =>
                onChange((prev) =>
                  prev.includes(n)
                    ? prev.filter((x) => x !== n)
                    : // Chan lan hai ngay trong ham cap nhat: hai cu bam sat nhau
                      // deu thay danh sach cu nen deu tuong con cho.
                      prev.length >= config.pick
                      ? prev
                      : [...prev, n],
                )
              }
              aria-pressed={on}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className={styles.pickerFoot}>
        <span className={styles.muted}>
          Đã chọn {picked.length}/{config.pick}
        </span>
        <button
          className={styles.quickPick}
          onClick={() => onChange(() => randomPick(numbers, config.pick))}
        >
          Chọn nhanh
        </button>
      </div>
    </>
  );
}

function range(min: number, max: number): number[] {
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}

function randomPick(pool: readonly number[], count: number): number[] {
  const copy = [...pool];
  const out: number[] = [];
  while (out.length < count && copy.length > 0) {
    const index = Math.floor(Math.random() * copy.length);
    out.push(copy.splice(index, 1)[0]!);
  }
  return out.sort((a, b) => a - b);
}

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): JSX.Element {
  return (
    <div className={styles.summaryRow}>
      <span className={styles.muted}>{label}</span>
      <span className={['numeric', strong ? styles.summaryStrong : ''].filter(Boolean).join(' ')}>
        {value}
      </span>
    </div>
  );
}
